import {Component, ChangeDetectorRef, OnInit, ViewChild} from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, FormArray, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import * as _ from 'lodash';

import { BrandService } from 'shared/brand/brand.service';
import { ScorecardConnectorService } from 'shared/scorecard/scorecard-connector.service';
import { Config } from 'shared/config/env.config';
import { PartNumberService } from 'shared/part-number/part-number.service';
import { languageLookup } from 'shared/product/product-lookups';
import {getAvailableLanguages, getFieldByMap, getLookupTitle} from 'utils/utils';

import { descriptionSegmentData } from '../../segment-data/description-segment-data';
import { marketingSegmentData } from '../../segment-data/marketing-segment-data';
import { MarketingModel } from '../../models/marketing.model';
import { BaseSegmentListComponent } from '../base/_base-segment-list.component';
import { segmentAnimation } from '../base/animations';
import { rowData, contentData, collapseWidthPx } from './table-data';
import {marketingCodeLookup} from 'utils/lookups';
import {ActivatedRoute, Router} from '@angular/router';
import {MarketingCopyService} from 'shared/marketing-copy/marketing-copy.service';
import * as utils from 'utils/utils';
import {MARKETING_NAME} from '../../../global-pages/brand-information/variables';
import {MarketingCopyDetailsService} from 'shared/marketing-copy/marketing-copy-details.service';
import {MarketingFormComponent} from 'shared/marketing-form/marketing-form.component';
import {publishedMarketingCopyToast} from 'shared/toaster/toasts';
import {BRAND_NAME} from '../../../global-pages/account-information/variables';
import { takeWhile, delay } from 'rxjs/operators';
import objectHash from 'object-hash';

@Component({
  moduleId: module.id,
  selector: 'marketing-segment',
  templateUrl: 'marketing.component.html',
  styleUrls: ['../base/_base-segment.component.scss', 'marketing.component.scss'],
  animations: [segmentAnimation],
})
export class MarketingComponent extends BaseSegmentListComponent implements OnInit {
  rowData: any[] = rowData;
  contentData: any[] = contentData;
  collapseWidth = collapseWidthPx;

  // define in the Child
  declare segmentForm: UntypedFormGroup;
  segmentName: string = 'marketing'; // used in save request
  pathToScores: string = 'segments.marketing.tallies';
  controlClassPrefix: string = 'marketing-control-row-';

  segmentData: any = marketingSegmentData;

  availableDescrTypes: Array<string> = ['SHO', 'DES', 'INV', 'EXT', 'ABR', 'LAB'];
  globalMarketingData: Array<any>;

  name: string = MARKETING_NAME;
  omitFields: Array<string> = [
    'languages', 'data', 'languageCode',
    'selectedLanguage', 'value', 'codeLookup'
  ];
  lookups: any = {};

  @ViewChild(MarketingFormComponent, {static: true})
  protected formComponent: MarketingFormComponent;

  protected isPageActive: boolean = true;
  data: any;
  sourceData: any;


  constructor(
    protected ref: ChangeDetectorRef,
    protected scorecardConnectorService: ScorecardConnectorService,
    protected brandService: BrandService,
    protected fb: UntypedFormBuilder,
    protected partNumberService: PartNumberService,
    protected sanitizer: DomSanitizer,
    private router: Router,
    private marketingCopyService: MarketingCopyService,
    private marketingCopyDetailsService: MarketingCopyDetailsService,
    private route: ActivatedRoute,
  ) {
    super(ref, scorecardConnectorService, brandService, fb, sanitizer);
  }

  /*******************************************
   * COMPONENT PUBLIC METHODS
   *******************************************/

  public getSegmentFormData(): any {
    const marketing = this.formArray.value;
    return _.map(marketing, (item: any) => {
      return _.pick(item, [
        'name', 'code', 'reference',
        'value', 'subCodeReference', 'type',
      ]);
    });
  }

  public toggleViewEditMode(toggle: boolean): void {
    if (!toggle) {
      return;
    }
    const expandedIDs = this.formComponent.expandedElementsIndexes;
    const expandedRows = this.data.filter((row, index) => expandedIDs.includes(index));
    const expandedRowsString = expandedRows.map((row) => {
      // ['name', 'reference', 'code', 'type']
      const obj = {
        name: row.name,
        reference: row.reference,
        code: row.code,
        type: row.type
      };
      return JSON.stringify(obj);
    });
    const expandedRowsHash = expandedRowsString.map(str => objectHash.MD5(str));
    this.router.navigate(
      ['global-brand-information/marketing'],
      {
        queryParams: {
          ids: expandedRowsHash,
          isFromProduct: true
        }
      }
    );
  }

  /*******************************************
   * COMPONENT HANDLERS
   *******************************************/


  /*******************************************
   * COMPONENT PROTECTED METHODS
   *******************************************/


  // replace parent method:
  protected handleDataItem(item: any) {
    const handledDataItem = {...item};

    handledDataItem.availableDescrTypes = this.getAvailableDescrTypes(item.partData);
    handledDataItem.availableDescrList = item.partData.descriptions;
    this.handleSelectedDescriptionType(handledDataItem);

    return handledDataItem;
  }

  // replace parent method:
  protected createItemModel(item: any): MarketingModel {
    const model = new MarketingModel();

    _.each(this.segmentData, (field: any) => {
      if (!field.field_map) { return; }

      // add typeahead to the model
      if (_.has(field, 'api_lookup.lookup')) {

        _.set(model, field.api_lookup.field_map, new UntypedFormControl(_.get(field.api_lookup, 'default_value', '')) );
      }

      const validators: any = this.getControlValidators(field);
      const value = _.get(item, field.item_name, '');
      _.set(model, field.field_map, new UntypedFormControl(value, validators));
    });
    // add photo value
    _.set(model, 'photo', new UntypedFormControl(_.get(item, 'photo', ''), null));
    _.set(model, 'availableDescrList', new UntypedFormControl(_.get(item, 'availableDescrList', [])));
    _.set(model, 'availableDescrTypes', new UntypedFormControl(_.get(item, 'availableDescrTypes', [])));
    _.set(model, 'availableDescrLangs', new UntypedFormControl( _.get(item, 'availableDescrLangs', [])));

    return model;
  }

  // replace parent method:
  protected createItem() {
    return {
      component_brand: null,
      component_brand_label: null,
      component_part_number: {id_qualifier: null, value: null},
      component_sub_brand: null,
      component_sub_brand_label: null,
      description: {language: null, type: null, value: null},
      part_terminology_id: null,
      part_type: null,
      quantity_in_kit: {uom: null, quantity: null},
    };
  }
  /*******************************************
   * COMPONENT PRIVATE METHODS
   *******************************************/

  private updateForm(): Observable<any> {
    const subject = new Subject();

    // create formArray
    this.segmentForm.setControl(this.segmentName, this.fb.array([]));

    const requestArray = [];
    const interval = 100;
    let timer = 0;

    _.forEach(this.itemDetailData, (item: any, i: number) => {
      const partNumber = _.get(item, 'component_part_number.value');

      requestArray.push(this.partNumberService.fetch(this.brandService.getBrand(), partNumber).pipe(delay(timer)));
      /* throttle the calls so we do not reach the servers throttle limit */
      timer = interval * i++;
    });

    forkJoin(requestArray)
      .pipe(takeWhile(() => this.isActive))
      .subscribe((partData: any[]) => {
        // handle each data item
        this.itemDetail[this.segmentName] = this.itemDetailData.map((item: any, index: number) => {
          item.partData = partData[index];
          return this.handleDataItem(item);
        });

        // add data item to formArray
        _.forEach(this.itemDetailData, (item: any) => {
          this.formArray.push(this.createItemFG(item));
        });
        this.ref.markForCheck();
        subject.next();
      });

    return subject.asObservable();
  }

  private getDescrSegmentValues(types: Array<string>): Array<any> {
    const descrSegmentValues = _.filter(descriptionSegmentData, (description: any) => {
      return (
        _.indexOf(this.availableDescrTypes, description.field_map.toUpperCase()) > -1 &&
        _.indexOf(types, description.field_map.toUpperCase()) > -1
      );
    });
    return descrSegmentValues;
  }

  private getLanguagesLookup(languages: Array<string>): Array<any> {
    return _.intersectionWith(languageLookup, languages, (lookup, lang) => {
      return lookup.option === lang;
    });
  }

  // if description type was selected - set the languages list
  private handleSelectedDescriptionType(item: any) {
    const descriptionType = _.get(item, 'description.type');
    if (descriptionType && item.partData.descriptions) {
      item.availableDescrLangs = this.getAvailableDescrLangs(item.partData, descriptionType);
      this.ref.markForCheck();
    }
  }

  // get list of available description types from partData
  // e.g. [{option: "SHO", title: "Short description"}, {option: "INV", title: "Invoice description"}]
  private getAvailableDescrTypes(partData: any) {
    const descrTypes = _.keys(partData.descriptions);
    const descrSegmentValues: Array<any> = this.getDescrSegmentValues(descrTypes);
    return _.map(descrSegmentValues, (descrSegmentValue: any) => {
      return {
        option: descrSegmentValue.field_map.toUpperCase(),
        title: descrSegmentValue.title,
      };
    });
  }

  // get list of available description types based on partData and selected descriptionType
  private getAvailableDescrLangs(partData: any, descriptionType: string) {
    const langKeys = _.keys(_.get(partData, `descriptions.${descriptionType}`));
    return this.getLanguagesLookup(langKeys);
  }

  getReferenceLabel(item: any): string {
    const itemLookup = this.lookups[item.code];
    const value = item.reference;
    const selectedLookup = _.find(itemLookup, (i: any) => String(i.option) === String(value));
    return selectedLookup ? selectedLookup.title : value;
  }
  private getItemCurrLangData(item: any): any {
    return _.find(item.data, {language_code: item.selectedLanguage});
  }
  getCurrentCopies(item: any): any {
    const langData: any = this.getItemCurrLangData(item);
    return langData ? langData.copies : [];
  }

  /*******************************************
   * COMPONENT GETTERS
   *******************************************/

  // show action panel always
  get showActionPanel(): boolean {
    return true;
  }

  // redefined method
  get showPublishAction(): boolean {
    return !this.isDirty;
  }

  /*******************************************
   * COMPONENT HOOKS
   *******************************************/

  ngOnInit() {
    super.ngOnInit();
    // this.brandId = this.brandService.getBrand();
    this.refresh();
  }

  /*******************************************
   * COMPONENT PUBLIC METHODS
   *******************************************/

  setData(data: any) {
    this.fetchAssignmentLookups()
      .pipe(takeWhile(() => this.isPageActive))
      .subscribe((lookups: Array<any>) => {
        this.data = this.reorganizeMarketingData(data);
        this.sourceData = [...this.data];
        _.forEach(this.data, item => {
          if (_.find(item.data, {language_code: 'EN'})) {
            item.selectedLanguage = 'EN';
          } else {
            item.selectedLanguage = item.data[0].language_code;
          }
        });
        this.ref.markForCheck();
        this.hideLoader();
      });
  }

  fetchFormData(): Observable<any> {
    return this.marketingCopyService.fetchMarketCopy(this.brandId);
  }

  putFormData(publish: boolean= false): Observable<any> {
    const data = this.prepareDataForSave(this.formComponent.marketingFormData.value);
    return this.marketingCopyService.putMarketCopy(this.brandId, data, publish);
  }

  /*******************************************
   * COMPONENT PRIVATE METHODS
   *******************************************/

  // group marketing data by by the name, reference, code, and type
  // sort by Reference Type(code prop) and Assignment(reference prop). Order is taken from lookups
  private reorganizeMarketingData(data: Array<any>): Array<any> {
    const fields = ['name', 'reference', 'code', 'type'];
    const groups: any = _.chain(data)
      .reduce((res: Array<any>, item: any) => {
        const common = _.pick(item, fields);
        // find element with the same fields
        const matchEl = _.find(res, common);
        if (matchEl) {
          // if this element exists - extend copies
          this.extendMarketingItem(matchEl, item);
        } else {
          // if not - create new
          const newItem = this.createMarketingItem(item);
          res.push(newItem);
        }
        return res;
      },  [])
      .groupBy('code')
      .value();
    // sort items inside groups by Assignment (reference prop)
    return _.chain(marketingCodeLookup)
      .map((code: any) => {
        return _.sortBy(groups[code.option], (item: any) => {
          return _.findIndex(this.lookups[code.option], ['option', item.reference]);
        });
      })
      .flatten()
      .value();
  }

  private extendMarketingItem(item: any, addItem: any) {
    // find item with the same language
    const matchLang: any = _.find(item.data, {language_code: addItem?.language_code});
    if (matchLang) {
      // if this element exists - extend it
      this.addDataToMarketingSubitem(matchLang, addItem);
    } else {
      // if not - create new
      const marketingSubitem = this.createMarketingSubitem(addItem.language_code);
      this.addDataToMarketingSubitem(marketingSubitem, addItem);
      item.data.push(marketingSubitem);
    }
  }

  private createMarketingSubitem(language_code: string): any {
    return {
      language_code,
      copies: <any>[],
    };
  }

  private addDataToMarketingSubitem(marketingSubitem: any, item: any) {
    if (item?.value !== null && item?.value !== undefined) {
      //  copy:
      //  {
      //    value: String,
      //    origin_id: String,
      //  },
      marketingSubitem.copies.push({
        value: item.value,
        origin_id: item.origin_id ? item.origin_id : null,
      });
    }
  }

  private addLanguage(item: any, language_code: string): void {
    // add all available languages (for now it's english, french, spanish)
    if (item.languages) { return; }
    item.languages = getAvailableLanguages();
  }

  private createMarketingItem(item: any = {
    name: '',
    reference: '',
    type: '',
    code: '',
    sub_code : '',
    sub_code_reference: '',
    language_code: 'EN',
    selectedLanguage: 'EN',
    value: '',
    languages: [],
    data: [{
      copies: [],
      language_code: 'EN',
    }],
  }) {
    const newItem = Object.assign({}, item);
    const marketingSubitem = this.createMarketingSubitem(item.language_code);
    this.addDataToMarketingSubitem(marketingSubitem, item);
    // add data field - array with the grouping by languages:
    // {
    //  copies: [
    //  {
    //    value: String,
    //    origin_id: String,
    //  },
    //  language_code: String,
    // }
    newItem.data = [marketingSubitem];
    this.addLanguage(newItem, item.language_code);
    return newItem;
  }

  private prepareDataForSave(data: any): any {
    return _.reduce(data, (result: Array<any>, item: any) => {
      // omit excess fields
      const obj: any = _.omit(item, this.omitFields);
      // create object with data for every copy value
      _.each(item.data, (copiesData: any) => {
        obj.languageCode = copiesData.languageCode;
        // remove copies with empty value
        _.remove(copiesData.copies, (copy: any) => !copy.value);
        if (copiesData.copies.length) {
          _.each(copiesData.copies, (copy: any) => {
            const dataItem = this.createDataItem(obj, copy);
            result.push(dataItem);
          });
        } else {
          // create empty item with english language only
          if (copiesData.languageCode !== 'EN') { return; }
          const dataItem = this.createDataItem(obj, {
            value: '',
            originId: null,
          });
          result.push(dataItem);
        }
      });
      return result;
    }, []);
  }

  private createDataItem(obj: any, copy: any): any {
    const dataItem = _.clone(obj);
    dataItem.value = copy.value;
    dataItem.originId = copy.origin_id;
    dataItem.digitalAssets = obj.digitalAssets.map(asset => asset.asset);
    if (!dataItem.brandRelationshipId) {
      dataItem.brandRelationshipId = this.brandId;
    }
    return dataItem;
  }

  private createCopy(text: string, originId: string): any {
    return {
      value: text,
      originId,
    };
  }

  private fetchAssignmentLookups(): Observable<any> {
    if (_.size(this.lookups)) { return of(this.lookups); }
    const subj = new Subject<any>();
    _.each(marketingCodeLookup, (item: any, index: number) => {
      this.marketingCopyService.fetchLookup(this.brandId, item.option)
        .subscribe((data: any = []) => {
          this.lookups[item.option] = data;
          if (index === marketingCodeLookup.length - 1) {
            subj.next(this.lookups);
            subj.complete();
          }
        });
    });
    return subj.asObservable();
  }

  refresh(): void {
    // this.refreshSegmentData();
    this.showLoader();
    this.refreshData().subscribe((res: any) => {
      const detailsMarketingcopyIds = res[0].map(item => item.origin_id);
      const data = res[1].filter(item => detailsMarketingcopyIds.indexOf(item.origin_id) !== -1);
      this.setData(data);
    });
  }

  protected showLoader() {
    this.formComponent.loader.show();
  }

  protected hideLoader() {
    this.formComponent.loader.hide();
  }


  protected refreshData(): Observable<any> {
    const { id } = this.route.snapshot.params;
    return forkJoin(
      this.marketingCopyDetailsService.fetchMarketingCopy(this.brandId, id),
      this.marketingCopyService.fetchMarketCopy(this.brandId)
    )
      .pipe(takeWhile(() => this.isActive));
  }
}
